"use client";

import { Clock, Mail, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hubs = [{
  code: "ZONE / 01 / DUR",
  name: "Durham, NC",
  region: "TRIANGLE CENTRAL",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 02 / RAL",
  name: "Raleigh, NC",
  region: "TRIANGLE EAST",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 03 / CRY",
  name: "Cary, NC",
  region: "TRIANGLE WEST",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 04 / CHH",
  name: "Chapel Hill, NC",
  region: "TRIANGLE WEST",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 05 / WKF",
  name: "Wake Forest, NC",
  region: "TRIANGLE NORTH",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 06 / HIL",
  name: "Hillsborough, NC",
  region: "TRIANGLE NORTH",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}, {
  code: "ZONE / 07 / MOR",
  name: "Morrisville, NC",
  region: "TRIANGLE CENTRAL",
  phone: "(919) 896-1953",
  email: "attictobasement@gmail.com",
  hours: "Mon–Fri 8a–8p"
}];
export default function WhereWeWork() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: pillRef,
    isVisible: pillVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const firstRow = hubs.slice(0, 4);
  const secondRow = hubs.slice(4, 7);
  return <section data-section-id="823" id="where-we-work" className="bg-background text-foreground py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="inline-block size-1.5 rounded-md bg-primary animate-pulse" />
            <span className="font-mono text-xs tracking-[0.15em] uppercase text-muted-foreground">
              Local Defense Network
            </span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-primary mb-4 block">
            Protecting Homes. Protecting Families. It&apos;s Our Promise.
          </p>
          <h1 className="font-serif font-default text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-foreground">
            Seven cities. <span className="italic font-light text-primary">Total protection.</span>
          </h1>
          <p className="mt-6 font-default text-base text-muted-foreground max-w-xl mx-auto">
            Elite pest management across the Triangle, tailored to every home.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {firstRow.map((hub, i) => <HubCard key={hub.code} hub={hub} index={i} dataIndex={i} />)}
        </div>

        <div ref={pillRef} data-index={0} className={`relative my-10 flex justify-center transition-all duration-700 ease-out ${pillVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-border hidden md:block" />
          <Link to={`tel:${"919-896-1953".replace(/[^0-9+]/g, "")}`} className="relative inline-flex items-center gap-3 rounded-md bg-primary px-6 py-3 font-mono text-[11px] md:text-xs tracking-[0.18em] uppercase text-primary-foreground hover:bg-secondary hover:text-secondary-foreground transition-all">
            <span>Questions? Call us</span>
            <span className="inline-block size-1.5 rounded-md bg-primary-foreground animate-pulse" />
          </Link>
        </div>

        <div className="flex flex-wrap justify-center gap-5">
          {secondRow.map((hub, i) => <div key={hub.code} className="w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(25%-0.9375rem)]">
              <HubCard hub={hub} index={i + 4} dataIndex={i + 4} />
            </div>)}
        </div>
      </div>
    </section>;
}
type Hub = {
  code: string;
  name: string;
  region: string;
  phone: string;
  email: string;
  hours: string;
};
function HubCard({
  hub,
  index,
  dataIndex
}: {
  hub: Hub;
  index: number;
  dataIndex: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const telHref = `tel:${hub.phone.replace(/[^0-9+]/g, "")}`;
  const mailHref = `mailto:${hub.email}`;
  return <div ref={ref} data-index={dataIndex} style={{
    transitionDelay: `${index * 60}ms`
  }} className={`group relative bg-card border border-border rounded-md p-7 flex flex-col justify-between transition-all duration-700 ease-out hover:-translate-y-0.5 hover:border-primary/50 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground group-hover:text-primary transition-colors">
            {hub.code}
          </span>
          <span className="inline-block size-1.5 rounded-md bg-primary animate-pulse" />
        </div>

        <div className="mt-5">
          <h3 className="font-serif font-default text-[26px] leading-tight tracking-tight text-foreground">
            {hub.name}
          </h3>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            {hub.region}
          </p>
        </div>

        <div className="my-5 h-px w-full bg-border" />

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Phone className="size-3.5 text-primary shrink-0" />
            <Link to={telHref} className="font-serif font-default text-[17px] text-foreground hover:text-primary transition-colors">
              {hub.phone}
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <Mail className="size-3.5 text-primary shrink-0" />
            <Link to={mailHref} className="font-mono text-[12px] text-muted-foreground hover:text-foreground transition-colors break-all">
              {hub.email}
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="size-3.5 text-primary shrink-0" />
            <span className="font-mono text-[12px] text-muted-foreground">
              {hub.hours}
            </span>
          </div>
        </div>
      </div>

      <div>
        <div className="my-5 h-px w-full bg-border" />
        <div className="flex items-center justify-end">
          <Link to={telHref} className="font-mono text-[11px] tracking-[0.1em] text-primary hover:text-foreground transition-colors whitespace-nowrap">
            Direct line →
          </Link>
        </div>
      </div>
    </div>;
}